import type { ChatSummary } from '../../api/types';
import type { ChatListViewFilter, ChatListViewSort } from '../chatListViewPrefsStorage';

/**
 * Чистая логика списка чатов, вынутая из `ChatListScreen.tsx` (ARCH-1).
 *
 * Экран — 3 500 строк одного компонента, и всё это жило внутри его замыкания: проверить, что
 * список сортируется в правильном порядке или что подпись под чатом собирается верно, можно
 * было только глазами на устройстве. Здесь ни состояния, ни React — только вход и выход.
 */

/** Маркер темы/набора звуков в подписи последнего сообщения: `[[kb:stem]]` или `[[hds:stem]]`. */
export type ChatPreviewMarker = { kind: 'kb' | 'hds'; stem: string };

export type ResolvedChatPreview = { text: string; marker: ChatPreviewMarker | null };

const PREVIEW_MARKER_RE = /^\[\[(kb|hds):([a-z0-9_-]+)]]$/;
const PREVIEW_MARKER_GLOBAL_RE = /\[\[(kb|hds):([a-z0-9_-]+)]]/g;

export function parseMarkerPreview(value: string): ChatPreviewMarker | null {
  const m = value.match(PREVIEW_MARKER_RE);
  if (!m) return null;
  return { kind: m[1] === 'hds' ? 'hds' : 'kb', stem: m[2] };
}

/**
 * Последний маркер в строке — именно последний, а не первый: подпись показывает то, чем
 * сообщение закончилось.
 */
export function extractLastMarker(value: string): ChatPreviewMarker | null {
  let last: ChatPreviewMarker | null = null;
  // Своя копия регулярки: у глобальной есть `lastIndex`, и переиспользование общей между
  // вызовами заставляло бы её продолжать с середины предыдущей строки.
  const re = new RegExp(PREVIEW_MARKER_GLOBAL_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    last = { kind: m[1] === 'hds' ? 'hds' : 'kb', stem: m[2] };
  }
  return last;
}

/**
 * Подпись под чатом. Поля превью приходили от бэкенда под разными именами по мере развития
 * API, поэтому кандидаты перебираются по порядку и берётся первый непустой.
 */
export function resolveChatPreview(chat: ChatSummary, fallback: string): ResolvedChatPreview {
  const raw = chat as ChatSummary & {
    lastMessageText?: unknown;
    lastMessagePreview?: unknown;
    lastMessage?: unknown;
  };
  const direct = [chat.lastMessagePreview, raw.lastMessageText, raw.lastMessagePreview, raw.lastMessage];
  for (const candidate of direct) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      const trimmed = candidate.trim();
      const marker = parseMarkerPreview(trimmed);
      if (marker) return { text: '', marker };
      const lastMarker = extractLastMarker(trimmed);
      const textWithoutMarkers = trimmed.replace(PREVIEW_MARKER_GLOBAL_RE, '').replace(/\s{2,}/g, ' ').trim();
      if (textWithoutMarkers.length > 0) {
        return { text: textWithoutMarkers, marker: lastMarker };
      }
      if (lastMarker) return { text: '', marker: lastMarker };
    }
  }
  return { text: fallback, marker: null };
}

/**
 * «Избранное» среди чатов пользователя, если сервер его уже завёл (эпик 8, стори 8.2).
 *
 * Оно приходит обычным чатом — владелец в нём участник, — но местом в общем списке быть не
 * должно: у него нет ни заголовка от сервера, ни собеседника, и сортировка по дате уносила бы
 * личное пространство куда-то в середину. Экран рисует его закреплённой карточкой сверху.
 */
export function savedChat(items: ChatSummary[]): ChatSummary | null {
  return items.find((chat) => chat.type === 'SAVED') ?? null;
}

/**
 * Видимый список: фильтр по типу чата плюс сортировка.
 *
 * @param prefsHydrated прочитаны ли настройки из хранилища. Пока нет — список пуст, и это
 *   намеренно: отрисовать на дефолтных ALL/RECENT, а затем применить сохранённые значит
 *   «перетряхнуть» список на глазах у человека, как будто экран загрузился второй раз. Чаты
 *   всё равно приходят по сети позже, так что времени до первого кадра это не добавляет.
 */
export function visibleChats(
  items: ChatSummary[],
  filter: ChatListViewFilter,
  sort: ChatListViewSort,
  prefsHydrated: boolean,
): ChatSummary[] {
  if (!prefsHydrated) {
    return [];
  }
  // «Избранное» из общего списка исключено при любом фильтре: у него своя карточка сверху, и
  // строкой без заголовка оно выглядело бы пустым чатом непонятно с кем.
  const listed = items.filter((chat) => chat.type !== 'SAVED');
  const filtered = filter === 'ALL' ? listed : listed.filter((chat) => chat.type === filter);
  // Копия перед sort(): он сортирует на месте, а `items` — это состояние экрана.
  return [...filtered].sort((a, b) => {
    if (sort === 'TITLE_ASC') {
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
    }
    if (sort === 'TITLE_DESC') {
      return b.title.localeCompare(a.title, undefined, { sensitivity: 'base' });
    }
    // Свежесть чата — его последнее сообщение, а не дата создания: иначе старый диалог со
    // свежим сообщением оставался внизу, и его легко было не заметить. Чат без сообщений
    // (и сервер старее этого поля) считается по дате создания.
    const aTs = Date.parse(a.lastMessageAt || a.createdAt);
    const bTs = Date.parse(b.lastMessageAt || b.createdAt);
    // Неразобранная дата — это не «сейчас»: неразличимый мусор должен оседать в конце
    // свежих и в начале старых, а не всплывать наверх списка.
    const aSafe = Number.isNaN(aTs) ? 0 : aTs;
    const bSafe = Number.isNaN(bTs) ? 0 : bTs;
    return sort === 'OLD' ? aSafe - bSafe : bSafe - aSafe;
  });
}

/**
 * Поиск по списку чатов (аудит удобства 2026-10-04, п. 1): по названию, без учёта регистра и
 * «ё/е», с любой части. Только по названию — превью последнего сообщения искать бесполезно:
 * сервер отдаёт одну строку, и «нашлось по старому сообщению» случается только случайно.
 *
 * <p>Порядок не меняется: поиск сужает уже отсортированный список, а не пересобирает его, —
 * иначе свежий чат, ради которого и искали, уезжал бы вниз.
 */
export function searchChats(items: ChatSummary[], query: string): ChatSummary[] {
  const q = fold(query.trim());
  if (!q) return items;
  return items.filter((chat) => fold(chat.title ?? '').includes(q));
}

function fold(value: string): string {
  return value.toLocaleLowerCase().replace(/ё/g, 'е');
}
